import { memo, useCallback, useEffect, useRef, useState } from "react"
import "./WelcomeTrucoDialog.css"
import ballaLogo from "../../../../../assets/logos/ballaGames_logo.png"

/* ─────────────────────────────────────────────────────────────────────────────
   WelcomeTrucoDialog
   ─────────────────────────────────────────────────────────────────────────────
   Props:
     isOpen      (bool)     — controls visibility
     onSubmit    (fn)       — called with (nickname: string) when form is submitted
     defaultValue (string)  — optional pre-fill (e.g. from localStorage)
───────────────────────────────────────────────────────────────────────────── */
function WelcomeTrucoDialog({ isOpen, onSubmit, defaultValue = "" }) {
    const [nickname, setNickname] = useState(defaultValue)
    const [error, setError] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const inputRef = useRef(null)

    /* Auto-focus the input whenever the dialog opens */
    useEffect(() => {
        if (isOpen) {
            const t = setTimeout(() => inputRef.current?.focus(), 120)
            return () => clearTimeout(t)
        }
    }, [isOpen])

    const validate = (val) => {
        const v = val.trim()
        if (!v) return "Please enter a nickname to continue."
        if (v.length < 2) return "Nickname must be at least 2 characters."
        if (v.length > 20) return "Nickname can't be longer than 20 characters."
        if (!/^[a-zA-Z0-9 _\-]+$/.test(v))
            return "Letters, numbers, spaces, _ and - only."
        return ""
    }

    const handleChange = useCallback((e) => {
        const val = e.target.value
        setNickname(val)
        // Clear error as soon as user starts correcting
        if (error) setError(validate(val))
    }, [error])

    const handleSubmit = useCallback((e) => {
        e?.preventDefault()
        const err = validate(nickname)
        if (err) { setError(err); inputRef.current?.focus(); return }

        setError("")
        setSubmitting(true)

        // Persist to localStorage so it pre-fills next visit
        localStorage.setItem("trucoNickname", nickname.trim())

        // Small delay gives the button animation a moment to breathe
        setTimeout(() => {
            setSubmitting(false)
            onSubmit?.(nickname.trim())
        }, 180)
    }, [nickname, onSubmit])

    /* Submit on Enter */
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") handleSubmit()
    }, [handleSubmit])

    if (!isOpen) return null

    return (
        <div className="wd-overlay" role="dialog" aria-modal="true" aria-label="Welcome to Truco">

            <div className="wd-dialog">

                {/* Gold top accent line */}
                <div className="wd-top-accent" />

                {/* ── Hero ── */}
                <div className="wd-hero">

                    {/* Gold card icon */}
                    <div className="wd-card-icon" aria-hidden="true">
                        {/* <svg viewBox="0 0 24 24">
                            <path d="M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm1-4.45l-.45.45c-.57.56-.93 1-.93 2h-1.5v-.5c0-.72.36-1.36.93-1.93l.62-.64c.28-.28.43-.65.43-1.03 0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5H8.1c0-1.66 1.34-3 3-3s3 1.34 3 3c0 .66-.26 1.26-.6 1.6z"/>
                        </svg> */}
                        <img src={ballaLogo} alt="" />
                    </div>

                    <span className="wd-welcome-label">Welcome to</span>
                    <h1 className="wd-title">TRUCO</h1>
                    <p className="wd-subtitle">
                        Choose your table name and step onto the felt.
                    </p>
                </div>

                {/* ── Divider ── */}
                <div className="wd-divider" />

                {/* ── Form ── */}
                <form className="wd-form" onSubmit={handleSubmit} noValidate>

                    <div className="wd-field">
                        <label className="wd-label" htmlFor="wd-nickname-input">
                            Your Nickname
                        </label>

                        <div className="wd-input-wrap">
                            {/* Person icon */}
                            <svg className="wd-input-icon" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                            </svg>

                            <input
                                id="wd-nickname-input"
                                ref={inputRef}
                                className={`wd-input${error ? " wd-input-error" : ""}`}
                                type="text"
                                autoComplete="username"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                maxLength={20}
                                placeholder="e.g. CardShark_99"
                                value={nickname}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                aria-describedby="wd-hint"
                                aria-invalid={!!error}
                                disabled={submitting}
                            />
                            <div className="wd-focus-line" aria-hidden="true" />
                        </div>

                        <span
                            id="wd-hint"
                            className={`wd-hint${error ? " wd-hint-error" : ""}`}
                            role={error ? "alert" : undefined}
                            aria-live="polite"
                        >
                            {error || "2–20 characters · letters, numbers, _ and -"}
                        </span>
                    </div>

                    <button
                        type="submit"
                        className="wd-submit"
                        disabled={submitting}
                        aria-busy={submitting}
                    >
                        <span className="wd-submit-inner">
                            {submitting ? "Entering…" : "Enter the Game"}
                            {!submitting && (
                                <svg className="wd-submit-arrow" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                                </svg>
                            )}
                        </span>
                    </button>

                </form>

            </div>
        </div>
    )
}

export default memo(WelcomeTrucoDialog)